import React, { useMemo } from 'react';
import TableWidget from './widgets/TableWidget';
import StatCardWidget from './widgets/StatCardWidget';
import GaugeWidget, { ProgressBarWidget } from './widgets/GaugeWidget';
import PresetWidget from './widgets/PresetWidget';
import { getWidgetViewKind, shouldSkipWidgetDataFetch, VIEW_KIND } from '../widgets/registry';
import { useWidgetData } from '../hooks/useWidgetData';
import { LoadingBlock } from '../../../shared/ui/composites';
import { cn } from '../../../lib/utils';
import { widgetRunPeriod } from '../utils/widgetPeriod';
import { widgetUsesReportQueries } from '../utils/reportQueries';
import { useReportData } from './ReportDataContext';

const ChartWidget = React.lazy(() => import('./widgets/ChartWidget'));

export default function WidgetRenderer({
  widget,
  period,
  profile,
  preview = false,
  previewData = null,
  chrome = true,
}) {
  const reportData = useReportData();
  const usesShared = !preview && widgetUsesReportQueries(widget);
  const skipDataFetch = shouldSkipWidgetDataFetch(widget.type) || preview || usesShared;
  const runPeriod = useMemo(
    () => widgetRunPeriod(widget, period),
    [widget, period],
  );
  const fetched = useWidgetData(widget, runPeriod, !skipDataFetch);
  const shared = usesShared ? reportData.dataForWidget(widget) : null;
  const data = preview ? previewData : (usesShared ? shared : fetched.data);
  const loading = preview ? false : (usesShared ? reportData.loading : fetched.loading);
  const error = preview ? null : (usesShared ? (shared?.error || reportData.error) : fetched.error);
  const viewKind = getWidgetViewKind(widget.type);

  if (skipDataFetch && !preview && shouldSkipWidgetDataFetch(widget.type)) {
    return <PresetWidget widget={widget} profile={profile} period={period} />;
  }

  if (preview && shouldSkipWidgetDataFetch(widget.type)) {
    return <PresetWidget widget={widget} profile={profile} period={period} />;
  }

  const isTable = viewKind === VIEW_KIND.table;
  const isStat = viewKind === VIEW_KIND.stat;
  const isProgress = viewKind === VIEW_KIND.progress || widget.type === 'progress_bar';
  const isGauge = viewKind === VIEW_KIND.gauge && !isProgress;
  const isChart = !isTable && !isStat && !isGauge && !isProgress;

  const appearance = widget.appearance || {};
  const bodyBg = appearance.bodyBg || undefined;
  const headerBg = appearance.headerBg || undefined;
  const headerFg = appearance.headerFg || undefined;
  const showHeader = chrome && appearance.showHeader !== false && !!widget.title;
  const showBorder = chrome && appearance.showBorder !== false;
  const showPadding = appearance.showPadding !== false;
  const fitContent = appearance.fitContent === true;

  return (
    <div
      className={cn(
        'w-full flex flex-col overflow-hidden',
        fitContent ? 'h-auto' : 'h-full',
        showBorder && 'rounded-2xl border border-slate-200/80 shadow-sm',
      )}
      style={{ background: bodyBg || (showBorder ? '#ffffff' : 'transparent') }}
    >
      {showHeader && (
        <div
          className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold shrink-0"
          style={{
            background: headerBg || undefined,
            color: headerFg || (headerBg ? '#ffffff' : undefined),
          }}
        >
          <span style={{ color: headerFg || (!headerBg ? '#334155' : undefined) }}>
            {widget.title}
          </span>
        </div>
      )}
      <div className={cn(
        fitContent ? 'flex-none' : 'flex-1 min-h-0',
        showPadding ? (chrome ? 'p-3' : 'p-2') : 'p-0',
      )}>
        {isTable && (
          <TableWidget widget={widget} data={data} loading={loading} error={error} compact={preview} />
        )}
        {isStat && (
          <StatCardWidget widget={widget} data={data} loading={loading} error={error} />
        )}
        {isGauge && (
          <GaugeWidget widget={widget} data={data} loading={loading} error={error} />
        )}
        {isProgress && (
          <ProgressBarWidget widget={widget} data={data} loading={loading} error={error} />
        )}
        {isChart && (
          <React.Suspense fallback={<LoadingBlock />}>
            <ChartWidget widget={widget} data={data} loading={loading} error={error} />
          </React.Suspense>
        )}
      </div>
    </div>
  );
}
